import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChecklistItem } from '../utils/readiness';
import { CheckCircle2, AlertCircle, AlertTriangle, Clock, ArrowRight, X } from 'lucide-react';
interface ReadinessChecklistProps {
  items: ChecklistItem[];
  open: boolean;
  onClose: () => void;
  onFixNow: (sectionId: string) => void;
}
export function ReadinessChecklist({
  items,
  open,
  onClose,
  onFixNow
}: ReadinessChecklistProps) {
  const getStatusIcon = (status: ChecklistItem['status']) => {
    switch (status) {
      case 'Complete':
        return <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-88" />;
      case 'Missing Info':
        return <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400" data-id="element-89" />;
      case 'Needs Review':
        return <Clock className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-90" />;
      case 'Blocked':
        return <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-91" />;
    }
  };
  const outstandingItems = items.filter(i => i.status !== 'Complete');
  const sortedItems = [...outstandingItems].sort((a, b) => {
    const order = {
      Blocked: 0,
      'Missing Info': 1,
      'Needs Review': 2,
      Complete: 3
    };
    return order[a.status] - order[b.status];
  });
  const handleFix = (sectionId: string) => {
    onClose();
    onFixNow(sectionId);
  };
  return <AnimatePresence data-id="element-92">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 z-40 bg-slate-900/40 dark:bg-slate-900/60" aria-hidden="true" data-id="element-93" />

          {/* Drawer */}
          <motion.aside role="dialog" aria-label="Readiness checklist" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.25,
        ease: 'easeInOut'
      }} className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col bg-white dark:bg-slate-900 shadow-2xl" data-id="element-94">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 px-5 py-4" data-id="element-95">
              <div className="flex items-center space-x-2" data-id="element-96">
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100" data-id="element-97">
                  Readiness Checklist
                </h2>
                <span className="rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-0.5 text-xs font-normal text-slate-500 dark:text-slate-400 shadow-sm" data-id="element-98">
                  {outstandingItems.length === 0 ? 'All clear' : `${outstandingItems.length} open`}
                </span>
              </div>
              <button onClick={onClose} aria-label="Close readiness checklist" className="rounded-md p-1.5 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300" data-id="element-99">
                <X className="h-5 w-5" data-id="element-100" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto" data-id="element-101">
              {sortedItems.length === 0 ? <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-id="element-102">
                  <CheckCircle2 className="h-10 w-10 text-green-500 dark:text-green-400" data-id="element-103" />
                  <p className="mt-3 text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-104">
                    Everything looks ready
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-105">
                    All readiness requirements have been met.
                  </p>
                </div> : <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-id="element-106">
                  {sortedItems.map(item => <li key={item.id} className="p-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50" data-id="element-107">
                      <div className="flex items-start space-x-3" data-id="element-108">
                        <div className="mt-0.5 flex-shrink-0" data-id="element-109">
                          {getStatusIcon(item.status)}
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-110">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-111">
                            {item.label}
                          </p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-112">
                            {item.explanation}
                          </p>
                          <button onClick={() => handleFix(item.sectionId)} className="group mt-2 flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300" data-id="element-113">
                            Fix Now
                            <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" data-id="element-114" />
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>}
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}